// components/ProjectDetail.jsx
import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronLeft, 
  faExpand, 
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
  faArrowsLeftRight,
  faCalendar,
  faRuler,
  faMapMarkerAlt,
  faDollarSign,
  faSpinner,
  faCompress,
  faExpandAlt,
  faAngleDoubleLeft,
  faAngleDoubleRight
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsData } from '../data/projectsData';

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [showBeforeAfter, setShowBeforeAfter] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [activeTab, setActiveTab] = useState('before-after');
  const [comparisonPosition, setComparisonPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [currentComparisonBefore, setCurrentComparisonBefore] = useState(null);
  const [currentComparisonAfter, setCurrentComparisonAfter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoFullscreen, setVideoFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const comparisonContainerRef = useRef(null);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Find the current project
  const currentProject = projectsData.find(project => project.slug === slug);

  // Reset comparison images when project changes
  useEffect(() => {
    if (currentProject) {
      setCurrentComparisonBefore(currentProject.beforeImages[0]);
      setCurrentComparisonAfter(currentProject.afterImages[0]);
      setComparisonPosition(50);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [currentProject]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setVideoPlaying(true);
    const handlePause = () => setVideoPlaying(false);
    const handleEnded = () => setVideoPlaying(false);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    // Handle fullscreen change
    const handleFullscreenChange = () => {
      setVideoFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Toggle video fullscreen
  const toggleVideoFullscreen = () => {
    if (!videoContainerRef.current) return;

    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (videoContainerRef.current.requestFullscreen) {
        videoContainerRef.current.requestFullscreen();
      } else if (videoContainerRef.current.webkitRequestFullscreen) {
        videoContainerRef.current.webkitRequestFullscreen();
      } else if (videoContainerRef.current.msRequestFullscreen) {
        videoContainerRef.current.msRequestFullscreen();
      }
      setVideoFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setVideoFullscreen(false);
    }
  };

  // Handle keyboard shortcuts for fullscreen video
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeTab === 'video' && videoFullscreen) {
        // Spacebar to play/pause
        if (e.code === 'Space') {
          e.preventDefault();
          handleVideoPlay();
        }
        // 'F' to exit fullscreen
        if (e.code === 'KeyF') {
          e.preventDefault();
          toggleVideoFullscreen();
        }
        // 'M' to toggle mute
        if (e.code === 'KeyM') {
          e.preventDefault();
          setMuted(!muted);
        }
        // Escape to exit fullscreen
        if (e.code === 'Escape') {
          if (videoFullscreen) {
            toggleVideoFullscreen();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, videoFullscreen, muted]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-yellow-600 text-4xl animate-spin mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Loading Project...</h2>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!currentProject) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md w-full mx-auto p-6 sm:p-8 bg-white rounded-lg shadow-lg">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-red-600 text-xl sm:text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">Project Not Found</h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">
            The project you're looking for doesn't exist or has been moved.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button 
              onClick={() => navigate('/projects')}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base"
            >
              Back to All Projects
            </button>
            <button 
              onClick={() => navigate('/')}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Set default values for missing properties
  const project = {
    ...currentProject,
    shortDescription: currentProject.shortDescription || currentProject.description.substring(0, 100) + '...',
    status: currentProject.status || 'Completed',
    year: currentProject.year || '2023',
    area: currentProject.area || 'N/A'
  };

  const allImages = [...project.beforeImages, ...project.afterImages];

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleVideoPlay = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const handleMouseMove = (moveEvent) => {
      if (!comparisonContainerRef.current) return;
      
      const container = comparisonContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const x = moveEvent.clientX - containerRect.left;
      const newPosition = (x / containerRect.width) * 100;
      
      setComparisonPosition(Math.max(0, Math.min(100, newPosition)));
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    
    const handleTouchMove = (moveEvent) => {
      if (!comparisonContainerRef.current || !moveEvent.touches[0]) return;
      
      const container = comparisonContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const x = moveEvent.touches[0].clientX - containerRect.left;
      const newPosition = (x / containerRect.width) * 100;
      
      setComparisonPosition(Math.max(0, Math.min(100, newPosition)));
    };
    
    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  const handleThumbnailClick = (img, isBefore) => {
    if (isBefore) {
      setCurrentComparisonBefore(img);
    } else {
      setCurrentComparisonAfter(img);
    }
    setComparisonPosition(50);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Header with Project Info */}
      <div className="w-full h-[300px] sm:h-[350px] md:h-[400px] mb-6 sm:mb-8 md:mb-10 header flex justify-center items-center relative" style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${project.afterImages[0]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="text-center px-4 sm:px-6 mt-8 sm:mt-10 relative z-10 max-w-4xl mx-auto mt-10" data-aos="zoom-in">
          <span className="inline-block bg-yellow-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-full font-semibold mb-3 sm:mb-4 text-xs sm:text-sm">
            {project.type}
          </span>
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white mb-4 sm:mb-6 px-2">
            {project.title}
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-white mb-6 sm:mb-8 px-2 line-clamp-2 sm:line-clamp-3">
            {project.description}
          </p>
          
      
        </div>
      </div>

      {/* Main Content */}
      <div className="px-3 sm:px-4 md:px-6 lg:px-8 xl:px-32 py-6 sm:py-8 md:py-10">
        {/* Back Button Only */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center space-x-1 sm:space-x-2 text-yellow-600 hover:text-yellow-700 font-semibold text-sm sm:text-base"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-xs sm:text-sm" />
            <span>Back to Projects</span>
          </button>
        </div>

        {/* Tab Navigation - Responsive */}
        <div className="mb-4 sm:mb-6 md:mb-8" data-aos="fade-up">
          <div className="flex flex-wrap gap-1 sm:gap-2 border-b border-gray-200 overflow-x-auto pb-1">
            <button
              className={`px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 font-semibold rounded-t-lg transition-colors whitespace-nowrap text-xs sm:text-sm md:text-base ${
                activeTab === 'before-after' 
                  ? 'bg-yellow-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setActiveTab('before-after')}
            >
              Before & After
            </button>
            <button
              className={`px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 font-semibold rounded-t-lg transition-colors whitespace-nowrap text-xs sm:text-sm md:text-base ${
                activeTab === 'gallery' 
                  ? 'bg-yellow-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setActiveTab('gallery')}
            >
              Photo Gallery
            </button>
            {project.video && (
              <button
                className={`px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 font-semibold rounded-t-lg transition-colors whitespace-nowrap text-xs sm:text-sm md:text-base ${
                  activeTab === 'video' 
                    ? 'bg-yellow-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setActiveTab('video')}
              >
                Project Video
              </button>
            )}
            <button
              className={`px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 font-semibold rounded-t-lg transition-colors whitespace-nowrap text-xs sm:text-sm md:text-base ${
                activeTab === 'features' 
                  ? 'bg-yellow-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setActiveTab('features')}
            >
              Features
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-4 sm:mt-6 md:mt-8" data-aos="fade-up">
          {activeTab === 'before-after' && currentComparisonBefore && currentComparisonAfter && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-4">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">
                  Transformation Showcase
                </h3>
                <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                  <span className={`font-semibold text-xs sm:text-sm ${showBeforeAfter ? 'text-gray-800' : 'text-gray-400'}`}>
                    Before
                  </span>
                  <button
                    onClick={() => setShowBeforeAfter(!showBeforeAfter)}
                    className="relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full bg-yellow-600 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 flex-shrink-0"
                    aria-label="Toggle before/after view"
                  >
                    <span className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform duration-200 ${
                      showBeforeAfter ? 'translate-x-4 sm:translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                  <span className={`font-semibold text-xs sm:text-sm ${!showBeforeAfter ? 'text-gray-800' : 'text-gray-400'}`}>
                    After
                  </span>
                </div>
              </div>

              {/* Before/After Comparison Slider */}
              <div 
                ref={comparisonContainerRef}
                className="relative mb-6 sm:mb-8 h-[250px] xs:h-[300px] sm:h-[350px] md:h-[400px] lg:h-[450px] xl:h-[500px] overflow-hidden rounded-lg shadow-xl"
              >
                <div className="relative w-full h-full">
                  <img
                    src={currentComparisonBefore}
                    alt="Before"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <img
                    src={currentComparisonAfter}
                    alt="After"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      clipPath: `inset(0 ${100 - comparisonPosition}% 0 0)`
                    }}
                  />
                  <div
                    className="absolute top-0 bottom-0 w-0.5 sm:w-1 bg-yellow-600 cursor-col-resize"
                    style={{ left: `${comparisonPosition}%` }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                  >
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-yellow-600 rounded-full flex items-center justify-center shadow-lg">
                      <FontAwesomeIcon icon={faArrowsLeftRight} className="text-white text-xs sm:text-sm" />
                    </div>
                  </div>
                  <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-yellow-600/90 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-lg">
                    <span className="font-semibold text-xs sm:text-sm">After</span>
                  </div>
                  <div className="absolute top-2 right-2 sm:top-4 sm:right-4  bg-black/70 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-lg">
                    <span className="font-semibold text-xs sm:text-sm">Before</span>
                  </div>
                </div>
                <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-black/70 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-lg">
                  <span className="font-semibold text-xs sm:text-sm">
                    {isMobile ? 'Slide to compare' : 'Drag slider to compare'}
                  </span>
                </div>
              </div>

              {/* Thumbnail Grid */}
              <div className="mt-6 sm:mt-8">
                <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
                  {showBeforeAfter ? 'Before Images' : 'After Images'}
                </h4>
                <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
                  {(showBeforeAfter ? project.beforeImages : project.afterImages).map((img, index) => (
                    <div
                      key={`${showBeforeAfter ? 'before' : 'after'}-${index}`}
                      className={`relative overflow-hidden rounded-lg shadow-md cursor-pointer transform transition-all duration-300 hover:scale-105 ${
                        (showBeforeAfter && img === currentComparisonBefore) || 
                        (!showBeforeAfter && img === currentComparisonAfter) 
                          ? 'ring-1 sm:ring-2 ring-yellow-600' 
                          : ''
                      }`}
                      onClick={() => handleThumbnailClick(img, showBeforeAfter)}
                    >
                      <img 
                        src={img} 
                        alt={`${showBeforeAfter ? 'Before' : 'After'} ${index + 1}`} 
                        className="w-full h-20 xs:h-24 sm:h-28 md:h-32 object-cover" 
                      />
                      <div className={`absolute inset-0 flex items-center justify-center ${
                        showBeforeAfter ? 'bg-black/40' : 'bg-yellow-600/40'
                      }`}>
                        <span className={`text-white font-bold px-1 py-0.5 sm:px-2 sm:py-1 rounded text-xs ${
                          showBeforeAfter ? 'bg-black/50' : 'bg-yellow-600/70'
                        }`}>
                          {showBeforeAfter ? 'BEFORE' : 'AFTER'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
                Project Gallery
              </h3>
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {allImages.map((img, index) => (
                  <div
                    key={index}
                    className="relative overflow-hidden rounded-lg shadow-md cursor-pointer group"
                    onClick={() => handleImageClick(img)}
                  >
                    <img 
                      src={img} 
                      alt={`Gallery ${index + 1}`} 
                      className="w-full h-40 xs:h-44 sm:h-48 md:h-56 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <FontAwesomeIcon icon={faExpand} className="text-white text-lg sm:text-xl md:text-2xl" />
                    </div>
                    <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 bg-black/70 text-white px-1 py-0.5 sm:px-2 sm:py-1 rounded text-xs">
                      {index < project.beforeImages.length ? 'Before' : 'After'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'video' && project.video && (
            <div className="relative">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
                Project Walkthrough
              </h3>
              
              {/* Video Container */}
              <div 
                ref={videoContainerRef}
                className={`relative rounded-lg overflow-hidden shadow-xl transition-all duration-300 ${
                  videoFullscreen 
                    ? 'fixed inset-0 z-[9999] bg-black' 
                    : 'bg-black'
                }`}
                style={{
                  height: videoFullscreen ? '100vh' : 'auto',
                  width: videoFullscreen ? '100vw' : '100%',
                  top: videoFullscreen ? '0' : 'auto',
                  left: videoFullscreen ? '0' : 'auto',
                  borderRadius: videoFullscreen ? '0' : '0.5rem'
                }}
              >
                <video
                  ref={videoRef}
                  src={project.video}
                  className={`w-full ${videoFullscreen ? 'h-full object-contain' : 'h-auto max-h-[70vh]'}`}
                  controls={false}
                  muted={muted}
                  loop
                  onClick={handleVideoPlay}
                />
                
                {/* Video Controls Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/50 to-transparent transition-opacity duration-300 hover:opacity-100 ${
                  videoPlaying && !videoFullscreen ? 'opacity-0' : 'opacity-100'
                }`}>
                  {/* Play/Pause Button - Centered */}
                  <button
                    onClick={handleVideoPlay}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/70 text-white p-2 sm:p-3 md:p-4 rounded-full hover:bg-black/90 transition-colors hover:scale-110"
                    aria-label={videoPlaying ? "Pause" : "Play"}
                  >
                    <FontAwesomeIcon icon={videoPlaying ? faPause : faPlay} className="text-lg sm:text-xl md:text-2xl" />
                  </button>
                  
                  {/* Bottom Controls Bar */}
                  <div className={`absolute bottom-0 left-0 right-0 p-2 sm:p-3 md:p-4 transition-all duration-300 ${
                    videoFullscreen ? 'bg-gradient-to-t from-black/80 to-transparent' : ''
                  }`}>
                    {/* Video Info */}
                    <div className="mb-1 sm:mb-2">
                      <h4 className="text-white font-semibold text-xs sm:text-sm md:text-base truncate">
                        {project.title} Walkthrough
                      </h4>
                      <p className="text-gray-300 text-xs hidden sm:block">
                        Click anywhere to play/pause
                      </p>
                    </div>
                    
                    {/* Control Buttons */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={handleVideoPlay}
                          className="bg-white/10 backdrop-blur-sm text-white p-1 sm:p-2 rounded-lg hover:bg-white/20 transition-colors"
                          aria-label={videoPlaying ? "Pause" : "Play"}
                        >
                          <FontAwesomeIcon icon={videoPlaying ? faPause : faPlay} className="text-xs sm:text-sm" />
                        </button>
                        <button
                          onClick={() => setMuted(!muted)}
                          className="bg-white/10 backdrop-blur-sm text-white p-1 sm:p-2 rounded-lg hover:bg-white/20 transition-colors"
                          aria-label={muted ? "Unmute" : "Mute"}
                        >
                          <FontAwesomeIcon icon={muted ? faVolumeMute : faVolumeUp} className="text-xs sm:text-sm" />
                        </button>
                        <span className="text-white text-xs ml-1 sm:ml-2">
                          {videoRef.current ? Math.floor(videoRef.current.currentTime || 0) : 0}s
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Fullscreen Toggle */}
                        <button
                          onClick={toggleVideoFullscreen}
                          className="bg-white/10 backdrop-blur-sm text-white p-1 sm:p-2 rounded-lg hover:bg-white/20 transition-colors"
                          aria-label={videoFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                          <FontAwesomeIcon icon={videoFullscreen ? faCompress : faExpandAlt} className="text-xs sm:text-sm" />
                        </button>
                        
                        {/* Exit Fullscreen Button - Only shown in fullscreen mode */}
                        {videoFullscreen && (
                          <button
                            onClick={toggleVideoFullscreen}
                            className="bg-red-600 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-lg hover:bg-red-700 transition-colors text-xs sm:text-sm"
                          >
                            Exit Fullscreen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Top Right Controls - Only in fullscreen */}
                  {videoFullscreen && !isMobile && (
                    <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex items-center space-x-1 sm:space-x-2">
                      <div className="bg-black/50 text-white px-2 py-1 rounded-full text-xs">
                        Press <kbd className="px-1 py-0.5 bg-gray-800 rounded">Space</kbd> to play/pause
                      </div>
                      <div className="bg-black/50 text-white px-2 py-1 rounded-full text-xs">
                        Press <kbd className="px-1 py-0.5 bg-gray-800 rounded">F</kbd> to exit
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Video Instructions */}
              <div className="mt-3 sm:mt-4 text-center">
                <p className="text-gray-600 mb-2 text-sm sm:text-base">
                  Watch the complete transformation process
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                  <span className="flex items-center justify-center gap-1">
                    <FontAwesomeIcon icon={faPlay} className="text-xs" />
                    Click video to play/pause
                  </span>
                  <span className="flex items-center justify-center gap-1">
                    <FontAwesomeIcon icon={faExpandAlt} className="text-xs" />
                    Click expand icon for fullscreen
                  </span>
                  <span className="flex items-center justify-center gap-1">
                    <FontAwesomeIcon icon={faVolumeUp} className="text-xs" />
                    Click speaker to toggle sound
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
                Project Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {project.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3 sm:space-x-4 bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-yellow-600 text-sm sm:text-base md:text-xl">✓</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 text-sm sm:text-base md:text-lg truncate">
                        {feature}
                      </h4>
                      <p className="text-gray-600 mt-1 text-xs sm:text-sm">
                        Expertly implemented for maximum quality and durability
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Project Info Card */}
        <div className="mt-8 sm:mt-10 md:mt-12 bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
            Project Details
          </h3>
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-500">Project Status</p>
              <p className={`font-semibold text-sm sm:text-base ${project.status === 'Completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                {project.status}
              </p>
            </div>
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-500">Completion Year</p>
              <p className="font-semibold text-gray-800 text-sm sm:text-base">{project.year}</p>
            </div>
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-500">Project Type</p>
              <p className="font-semibold text-gray-800 text-sm sm:text-base">{project.type}</p>
            </div>
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-500">Total Area</p>
              <p className="font-semibold text-gray-800 text-sm sm:text-base">{project.area}</p>
            </div>
          </div>
        </div>

        {/* Navigation to other projects */}
        {/* <div className="mt-8 sm:mt-10 md:mt-12">
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
            Other Projects
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {projectsData
              .filter(p => p.slug !== slug)
              .slice(0, 2)
              .map(project => (
                <div 
                  key={project.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer group hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/projects/${project.slug}`)}
                >
                  <div className="relative h-40 sm:h-48 md:h-56 overflow-hidden">
                    <img 
                      src={project.afterImages[0]} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors"></div>
                    <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-yellow-600 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-semibold">
                      {project.type}
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <h4 className="font-bold text-base sm:text-lg text-gray-800 mb-1 sm:mb-2 truncate">
                      {project.title}
                    </h4>
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">
                      {project.shortDescription || project.description.substring(0, 80) + '...'}
                    </p>
                    <div className="flex justify-between text-xs sm:text-sm text-gray-500">
                      <span className="truncate">{project.location}</span>
                      <span>{project.duration}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div> */}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage(null);
            }}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white text-2xl sm:text-3xl md:text-4xl hover:text-yellow-500 transition-colors z-10 p-1"
            aria-label="Close image"
          >
            ✕
          </button>
          <div 
            className="relative w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedImage} 
              alt="Enlarged view" 
              className="max-w-full max-h-full object-contain" 
            />
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white py-10 sm:py-12 md:py-16 px-3 sm:px-4 md:px-6 lg:px-8 xl:px-32 mt-8 sm:mt-10 md:mt-12">
        <div className="text-center" data-aos="fade-up">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 max-w-3xl mx-auto px-2">
            Inspired by our work? Let's discuss how we can bring your vision to life.
          </p>
          <button 
            onClick={() => navigate('/contact')}
            className="bg-white text-yellow-700 font-bold py-2 px-6 sm:py-3 sm:px-8 rounded-lg hover:bg-gray-100 transition-colors duration-300 text-sm sm:text-base md:text-lg"
          >
            Contact Us Today
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;